import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Slider,
  Button
} from '@mui/material';
import { Pause, PlayArrow, SkipPrevious, SkipNext, VolumeUp, VolumeOff } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Base64 encoded 1x1 transparent pixel as fallback
const placeholderImage = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

// Sample podcast data (you can replace with your actual data)
const podcasts = [
  // {
  //   id: 1,
  //   title: 'Understanding Mental Health Basics',
  //   description: 'In this episode, we discuss the fundamentals of mental health and wellbeing.',
  //   duration: '24:35',
  //   date: 'Sep 20, 2025',
  //   audioSrc: '/podcasts/mental-health-basics.mp3',
  //   image: placeholderImage
  // },
    {
  id: 1,
  title: 'Breaking Negative Thinking Cycle',
  description: 'In this audio, we discuss the ways to gently break the negative thinking cycle.',
  duration: '18:45',  // Update this with the actual duration of your audio file
  date: 'Dec 1, 2025',
  audioSrc: '/podcasts/Breaking Negative Thinking Cycle.mp3',
  image: placeholderImage
},
  // Add more episodes as needed
];

const PodcastCard = styled(Card)(({ theme }) => ({
  maxWidth: 800,
  margin: '2rem auto',
  borderRadius: 12,
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
  overflow: 'hidden',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)'
  }
}));

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

const LearnWithEcho = () => {
  const [currentPodcast, setCurrentPodcast] = useState(podcasts[0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);

  const audioRef = useRef(null);
  const navigate = useNavigate();

  const handlePlayPause = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleSeek = (event, newValue) => {
    audioRef.current.currentTime = newValue;
    setCurrentTime(newValue);
  };

  const handleVolumeChange = (event, newValue) => {
    setVolume(newValue);
    audioRef.current.volume = newValue;
    setIsMuted(newValue === 0);
  };

  const toggleMute = () => {
    if (isMuted) {
      audioRef.current.volume = volume || 0.7;
      setIsMuted(false);
    } else {
      audioRef.current.volume = 0;
      setIsMuted(true);
    }
  };

  const handleLoadedMetadata = () => {
    setDuration(audioRef.current.duration);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Learn with Echo
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Educational podcasts for your mental health journey
        </Typography>
      </Box>

      <PodcastCard>
        <CardMedia
          component="img"
          height="300"
          image={currentPodcast.image}
          alt={currentPodcast.title}
        />
        <CardContent>
          <Typography gutterBottom variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
            {currentPodcast.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {currentPodcast.description}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {currentPodcast.date} • {currentPodcast.duration}
          </Typography>

          {/* Audio Player */}
          <Box sx={{ mt: 3 }}>
            <audio
              ref={audioRef}
              src={currentPodcast.audioSrc}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={() => setIsPlaying(false)}
            />

            {/* Progress Bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {formatTime(currentTime)}
              </Typography>
              <Slider
                value={currentTime}
                max={duration || 100}
                onChange={handleSeek}
                sx={{ flexGrow: 1 }}
                aria-label="Audio progress"
              />
              <Typography variant="caption" color="text.secondary">
                {formatTime(duration)}
              </Typography>
            </Box>

            {/* Controls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <IconButton aria-label="previous podcast">
                  <SkipPrevious />
                </IconButton>
                <IconButton
                  aria-label={isPlaying ? 'pause' : 'play'}
                  onClick={handlePlayPause}
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    mx: 1
                  }}
                >
                  {isPlaying ? <Pause /> : <PlayArrow />}
                </IconButton>
                <IconButton aria-label="next podcast">
                  <SkipNext />
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 150 }}>
                <IconButton onClick={toggleMute} size="small">
                  {isMuted || volume === 0 ? <VolumeOff /> : <VolumeUp />}
                </IconButton>
                <Slider
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  min={0}
                  max={1}
                  step={0.01}
                  aria-label="Volume"
                />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </PodcastCard>

      {/* Episode List */}
      <Box sx={{ mt: 6, maxWidth: 800, mx: 'auto' }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          More Episodes
        </Typography>
        {podcasts.map((podcast) => (
          <Card key={podcast.id} sx={{ mb: 2, '&:hover': { bgcolor: 'action.hover' }, cursor: 'pointer' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 80, height: 80, borderRadius: 1, overflow: 'hidden' }}>
                <img
                  src={podcast.image}
                  alt={podcast.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'medium' }}>
                  {podcast.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {podcast.date} • {podcast.duration}
                </Typography>
              </Box>
              <IconButton>
                <PlayArrow />
              </IconButton>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Back Button */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          sx={{ textTransform: 'none', borderRadius: 2, px: 4 }}
        >
          Back to Home
        </Button>
      </Box>
    </Container>
  );
};

export default LearnWithEcho;
